import React from "react";
import Card from "@mui/material/Card";

import {
    monthNameShortString,
    onlyDateString,
    onlyDayNameString,
    onlyYearString
} from "../../../services/date/formatters";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import EuroIcon from '@mui/icons-material/Euro';
import Avatar from "@mui/material/Avatar";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import CountryFlag from "../../../components/CountryFlag";
import {amber, blue, green, grey} from "@mui/material/colors";
import PetsIcon from "@mui/icons-material/Pets";
import EditIcon from "@mui/icons-material/Edit";
import AlertTitle from "@mui/material/AlertTitle";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";


const BookingHeader = ({booking, onEdit}) => {

    return <Card sx={{mt: {md: 3}}}>
        <CardHeader title="Riepilogo"
                    titleTypographyProps={{color: "primary.main", variant: 'button'}}
                    sx={{pb:0}}
                    action={
                        <Button size="small"
                                color="primary"
                                onClick={onEdit}
                                endIcon={<EditIcon fontSize="small"/>}>
                            Modifica
                        </Button>
                    }/>
        <CardContent>
            <Grid container sx={{ alignItems: "center" }} spacing={4}>
                <Grid item md={3} xs={12}>
                    <StackedDateRange arrivalDate={booking.getArrival()} departureDate={booking.getDeparture()}/>
                </Grid>

                <Grid item md={6} xs={12}>
                    <Stack spacing={1} alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                            <Typography fontSize={18}>
                                {booking.getNumberOfNights()} Notti
                            </Typography>
                            <span>•</span>
                            <Typography fontSize={18}>
                                {booking.getGuestsSummary(true)}
                            </Typography>
                            {
                                booking.petsNumber > 0 &&
                                <>
                                    <span>•</span>
                                    <Typography fontSize={18}>
                                        {booking.petsNumber}
                                    </Typography>
                                    <PetsIcon fontSize="small"/>
                                </>
                            }
                        </Stack>
                        <Box direction="row" sx={{display: "flex", columnGap: 1, alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
                            {booking.nationality && <CountryFlag code={booking.nationality.code}/>}
                            <Typography fontWeight='light' fontSize={14}>
                                {booking.getFullName() ?? "Nessun nome"}
                            </Typography>
                            <Typography fontWeight='light' fontSize={14}>
                                {booking.email ?? "Nessuna email"}
                            </Typography>
                        </Box>
                        {
                            !booking.hostNotes && <Typography fontWeight='light' fontSize={14} fontStyle="italic">
                                Nessuna nota personale
                            </Typography>
                        }
                    </Stack>
                </Grid>
                <Grid item md={3} xs={12}>
                    <StackedPrice booking={booking}/>
                </Grid>

                {
                    !!booking.hostNotes && <Grid item xs={12}>
                        <Alert severity={"info"}>
                            <AlertTitle>Note Personali</AlertTitle>
                            {booking.hostNotes}
                        </Alert>
                    </Grid>
                }
            </Grid>
        </CardContent>
    </Card>
}

const StackedDateRange = props => {
    const {arrivalDate, departureDate, sx, small} = props;

    return (
        <Stack direction="row" spacing={1} sx={{...sx}} alignItems="center">
            <StackedDate date={arrivalDate} small={small}/>
            <Divider orientation="vertical" flexItem/>
            <StackedDate date={departureDate} small={small}/>
        </Stack>
    )
}

const StackedDate = props => {
    const {date, small} = props;
    const dayName = onlyDayNameString(date, 'cccc')
    const dayNumber = onlyDateString(date);
    const month = monthNameShortString(date);
    const year = onlyYearString(date)

    return (
        <Stack alignItems="center" flexGrow={1}>
            <Typography fontWeight='light' fontSize={14}>
                {dayName}
            </Typography>
            <Typography fontSize={small ? 20 : 28}>
                {dayNumber} {month}
            </Typography>
            <Typography fontSize={small ? 12 : 18} sx={{textTransform: 'uppercase'}}>
                {year}
            </Typography>
        </Stack>
    )
}

const colorStyle = {
    blue: {
        color: blue[400].contrastText,
        borderColor: blue[800],
        backgroundColor: blue[400],
    },
    amber: {
        color: amber[400].contrastText,
        borderColor: amber[800],
        backgroundColor: amber[400],
    },
    grey: {
        color: grey[400].contrastText,
        borderColor: grey[800],
        backgroundColor: grey[400],
    },
    green: {
        color: green[400].contrastText,
        borderColor: green[800],
        backgroundColor: green[400],
    }
}

const StackedPrice = ({booking}) => {
    var color;
    const bookingFinance = booking.bookingFinance;

    if (bookingFinance.deposit && bookingFinance.totalPayed < bookingFinance.deposit){
        // Deposit present but not payed
        color = colorStyle.amber
    } else if (bookingFinance.totalPrice && bookingFinance.totalPayed >= bookingFinance.totalPrice){
        // Price present and payed
        color = colorStyle.green
    } else{
        // Other cases
        color = colorStyle.blue
    }

    return <Stack direction="row" spacing={1} alignItems="center" justifyContent="end">
            <Avatar sx={{...color, width: 56, height: 56 }}>
                <EuroIcon />
            </Avatar>
            <Stack sx={{flexGrow: 1}}>
                <Typography fontSize={18} textAlign="end">
                    Totale: {Number(bookingFinance.totalPrice).toFixed(2) ?? '-.--'} €
                </Typography>
                <Typography fontWeight='light' fontSize={14} textAlign="end">
                    Caparra: {Number(bookingFinance.deposit).toFixed(2) ?? '-.--'} €
                </Typography>
                <Typography fontSize={14} textAlign="end" color="success.main">
                    Pagato: {Number(bookingFinance.totalPayed).toFixed(2) ?? '-.--'} €
                </Typography>
                <Typography fontSize={14} textAlign="end" color="info.main">
                    Da pagare: {Number(bookingFinance.balance).toFixed(2) ?? '-.--'} €
                </Typography>
            </Stack>
    </Stack>
}

export default BookingHeader;